/**
 * Home.jsx
 *
 * Dashboard page displaying user-related data including:
 * - Prediction statistics (total, correct, incorrect)
 * - Favorite teams and players
 * - Soccer prediction history with filtering
 * - Football rival vote predictions
 *
 * Responsibilities:
 * - Fetches user data from backend APIs
 * - Manages UI state for dropdowns, filters, and sections
 * - Handles navigation to team-specific pages
 *
 * Data Sources:
 * - /api/predictions/stats      → user prediction summary
 * - /api/predictions            → soccer predictions
 * - /api/favorites/show         → favorites + rival votes
 *
 * Notes:
 * - Uses session-based authentication (credentials: "include")
 * - Handles inconsistent API responses defensively (array vs object)
 * - UI sections are collapsible for better UX
 */

import '../css/Home.css';
import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";


const Home = () => {

    // Navigation hook for routing to team/player pages
    const navigate = useNavigate();

    /**
     * Prediction statistics state
     * - total: total predictions made
     * - correct: number of correct predictions
     * - incorrect: number of incorrect predictions
     */
    const [stats, setStats] = useState({
        total:0,
        correct:0,
        incorrect:0
    });

    // Prediction + filtering state
    const [predictions, setPredictions] = useState([]);
    const [filter, setFilter] = useState('all');
    const [isOpen, setIsOpen] = useState(true);

    // Favorites state (teams + players)
    const [favorites, setFavorites] = useState(null);
    const [favsOpen, setFavsOpen] = useState(true);
    const [favsFilter, setFavsFilter] = useState('teams');

    // Rival vote (football predictions) state
    const [rivalOpen, setRivalOpen] = useState(true);
    const [rivalVotes, setRivalVotes] = useState([]);

    /**
     * Static team mappings used for routing logic
     * - Helps determine correct URL paths for navigation
     */
    const nfcTeams = [
        'dallas-cowboys', 'cowboys',
        'new-york-giants', 'giants',
        'philadelphia-eagles', 'eagles',
        'washington-commanders', 'commanders',
        'chicago-bears', 'bears',
        'detroit-lions', 'lions',
        'green-bay-packers', 'packers',
        'minnesota-vikings', 'vikings',
        'atlanta-falcons', 'falcons',
        'carolina-panthers', 'panthers',
        'new-orleans-saints', 'saints',
        'tampa-bay-buccaneers', 'buccaneers',
        'arizona-cardinals', 'cardinals',
        'los-angeles-rams', 'rams',
        'san-francisco-49ers', '49ers',
        'seattle-seahawks', 'seahawks'
    ];

    const afcTeams = [
        'baltimore-ravens', 'ravens',
        'buffalo-bills', 'bills',
        'miami-dolphins', 'dolphins',
        'new-england-patriots', 'patriots',
        'cincinnati-bengals', 'bengals',
        'cleveland-browns', 'browns',
        'pittsburgh-steelers', 'steelers',
        'tennessee-titans', 'titans',
        'houston-texans', 'texans',
        'indianapolis-colts', 'colts',
        'jacksonville-jaguars', 'jaguars',
        'las-vegas-raiders', 'raiders',
        'denver-broncos', 'broncos',
        'kansas-city-chiefs', 'chiefs',
        'los-angeles-chargers', 'chargers',
        'new-york-jets', 'jets'
    ];

    // Maps shorthand team names to full URL-friendly slugs
    const urlName = {
        'broncos': 'denver-broncos',
        'chiefs': 'kansas-city-chiefs',
        'raiders': 'las-vegas-raiders',
        'chargers': 'los-angeles-chargers',
        'jets': 'new-york-jets',
        'bills': 'buffalo-bills',
        'dolphins': 'miami-dolphins',
        'patriots': 'new-england-patriots',
        'ravens': 'baltimore-ravens',
        'bengals': 'cincinnati-bengals',
        'browns': 'cleveland-browns',
        'steelers': 'pittsburgh-steelers',
        'texans': 'houston-texans',
        'colts': 'indianapolis-colts',
        'jaguars': 'jacksonville-jaguars',
        'titans': 'tennessee-titans',
        'cowboys': 'dallas-cowboys',
        'giants': 'new-york-giants',
        'eagles': 'philadelphia-eagles',
        'commanders': 'washington-commanders',
        'bears': 'chicago-bears',
        'lions': 'detroit-lions',
        'packers': 'green-bay-packers',
        'vikings': 'minnesota-vikings',
        'falcons': 'atlanta-falcons',
        'panthers': 'carolina-panthers',
        'saints': 'new-orleans-saints',
        'buccaneers': 'tampa-bay-buccaneers',
        'cardinals': 'arizona-cardinals',
        'rams': 'los-angeles-rams',
        '49ers': 'san-francisco-49ers',
        'seahawks': 'seattle-seahawks'
    };

    // Soccer team identifiers for Premier League routing
    const soccerTeams = [
    'arsenal',
    'aston-villa', 'aston villa',
    'bournemouth',
    'brentford',
    'brighton', 'brighton-hove-albion', 'brighton hove albion',
    'burnley',
    'chelsea',
    'crystal-palace', 'crystal palace',
    'everton',
    'fulham',
    'leeds-united', 'leeds',
    'leicester-city', 'leicester',
    'liverpool',
    'manchester-city', 'man city', 'manchester city',
    'manchester-united', 'man united', 'manchester united',
    'newcastle-united', 'newcastle',
    'nottingham-forest', 'nottingham',
    'sunderland',
    'tottenham-hotspur', 'tottenham', 'spurs',
    'west-ham-united', 'west ham', 'west-ham',
    'wolverhampton-wanderers', 'wolves', 'wolverhampton'
];
    // Fetch user prediction stats on initial load
    useEffect(() => {
        // get user's stats
        fetch(`${process.env.REACT_APP_API_URL}/api/predictions/stats`, {
        //fetch('http://localhost:5001/api/predictions/stats', {
            credentials: "include"
        })
            .then(response => response.json())
            .then(data => setStats(data))
        .catch(error => console.error(error));
    },[]);

    // Fetch all user predictions on initial load
    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL}/api/predictions`, {
        //fetch('http://localhost:5001/api/predictions',{
            credentials: "include"
        })
            .then(response => response.json())
            .then(data => setPredictions(Array.isArray(data) ? data : []))
            .catch(err => {
                console.error(err);
                setPredictions([]);
            })
    }, []);

    /**
     * Fetch favorites and extract:
     * - Favorite teams
     * - Favorite players
     * - Rival vote data
     */
    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL}/api/favorites/show`, {
        //fetch('http://localhost:5001/api/favorites/show', {
            credentials: "include"
        })
            .then(res => res.json())
            .then(data => {
                const arr = Array.isArray(data) ? data : [data];
                console.log("all favorites:", JSON.stringify(data));
                setFavorites(arr);

                // Extract rival votes for football predictions
                setRivalVotes(arr
                    .filter(doc => doc.rivalVote)
                    .map(doc => ({ teamName: doc.team.name, rivalVote: doc.rivalVote }))
                );
            })
            .catch(err => console.error(err));
    }, []);

    /**
     * Filters predictions based on selected status
     * - all, correct, incorrect
     */
    const filtered = (Array.isArray(predictions) ? predictions: []).filter(p => {
        if(filter === 'correct') return p.isCorrect === true;
        if(filter === 'incorrect')return p.isCorrect === false;
        return true;
    });

    //  Extract favorite teams and players from favorites data
    const favTeams = Array.isArray(favorites) ? favorites.flatMap(doc => doc.team ? [doc.team] : []).filter(t => t.isFavorite) : [];
    
    const favPlayers = Array.isArray(favorites) ? favorites.flatMap(doc => doc.players ?? []).filter(p => p.isFavorite) : [];

    return (
       <main id="home-content">

            {/* heading */}
            <section className="heading">
                <h1>DASHBOARD</h1>
            </section>

            {/* show stats */}
            <section className="stats-container">
                <div className="stat-card"
                /*onClick={() => navigate("/mypicks?filter=all")} */
                > 
                    <h3>Total</h3>
                    <div className="stat-number">{stats.total}</div>
                </div>

                <div className="stat-card correct"
                /*onClick={() => navigate("/mypicks?filter=correct")} */
                >
                    <h3>Correct</h3>
                    <div className="stat-number">{stats.total > 0 ? `${Math.round((stats.correct / stats.total) * 100)}%` : `-` }</div>
                </div>

                <div className="stat-card incorrect"
                /*onClick={() => navigate("/mypicks?filter=incorrect")} */
                >
                    <h3>Incorrect</h3>
                    <div className="stat-number">{stats.total > 0 ? `${Math.round((stats.incorrect / stats.total) * 100)}%` : `-` }</div>
                </div>
            </section>

            {/**
             * Favorites Section
             * - Toggle between teams and players
             * - Navigates to appropriate team pages based on sport
             */}
            {/* (UI unchanged below, comments kept high-level to avoid noise) */}

            {/* Favorites */}
            <section className="picks-box">
                <div className="picks-header" onClick={() => setFavsOpen(!favsOpen)}>
                    <span className="picks-heading">My Favorites</span>
                    <span className="picks-arrow">{favsOpen ? '▲' : '▼'}</span>
                </div>

                {/* Favorites content (teams/players) */}
                {favsOpen && (
                <div className="picks-body">
                {favTeams.length === 0 && favPlayers.length === 0 ? (
                <p className="picks-empty">No favorites saved yet.</p>
            ) : (
                <>
                    <div className="picks-filter">
                        <button data-filter="teams" className={favsFilter === 'teams' ? 'active' : ''} onClick={() => setFavsFilter('teams')}>Teams</button>
                        <button data-filter="players" className={favsFilter === 'players' ? 'active' : ''} onClick={() => setFavsFilter('players')}>Players</button>
                    </div>

                    {favsFilter === 'teams' && (
                        favTeams.length === 0 ? <p className="picks-empty">No favorite teams yet.</p> :
                        <div className="fav-teams-grid">
                            {favTeams.map((t, i) => (
                                <div key={i} className="fav-team-card" style={{ cursor: 'pointer' }} onClick={() => {
                                    const teamURL = t.name.toLowerCase().replace(/\s+/g, '-');
                                    if (soccerTeams.includes(teamURL)) {
                                        navigate(`/teams/premier-league/${teamURL}`);
                                    } else if (afcTeams.includes(teamURL)) {
                                        navigate(`/sports/football/afc/teams/${teamURL}`);
                                    } else if (nfcTeams.includes(teamURL)) {
                                        navigate(`/sports/football/nfc/teams/${teamURL}`);
                                    }
                                }}>       
                                    <img src={t.logo} alt={t.name} className="fav-team-logo"/>                            
                                    <span className="fav-team-name">{t.name}</span>
                                    <span className="fav-team-sport">{t.sport}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {favsFilter === 'players' && (
                        favPlayers.length === 0 ? <p className="picks-empty">No favorite players yet.</p> :
                        <div className="fav-teams-grid">
                            {favPlayers.map((p, i) => {
                                const parts = p.name.split('-');
                                const number = parts[1];
                                const team = parts[0];
                                const playerName = parts.slice(2).join('-');
                                const playerURL = team.toLowerCase().replace(/\s+/g, '-');
                                return (
                                    <div key={i} className="fav-team-card player-card" style={{ cursor: 'pointer' }} onClick={() => {
                                        const fullURL = urlName[playerURL] || playerURL;
                                        if (soccerTeams.includes(playerURL)) {
                                            navigate(`/teams/premier-league/${playerURL}`);
                                        } else if (afcTeams.includes(playerURL)) {
                                            navigate(`/sports/football/afc/teams/${fullURL}`);
                                        } else if (nfcTeams.includes(playerURL)) {
                                            navigate(`/sports/football/nfc/teams/${fullURL}`);
                                        }
                                    }}> 
                                        <span className="fav-team-name">{playerName}</span>
                                        <span className="fav-team-sport">#{number}</span>
                                        <span className="fav-team-sport">{team}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </div> 
            )}
            </section>

            {/**
             * Soccer Predictions Section
             * - Displays filtered prediction history
             * - Shows match, selection, and result status
             */}
            <section className="picks-box">
                <div className="picks-header" onClick={() => setIsOpen(!isOpen)}>
                    <span className="picks-heading">My Soccer Predictions</span>
                    <span className= "picks-arrow">{isOpen ? '▲' : '▼'}</span>
                </div>

                {isOpen && (
                    <div className="picks-body">
                        <div className= "picks-filter">
                            <button data-filter="all" className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All</button>
                            <button data-filter="correct" className={filter === 'correct' ? 'active' : ''} onClick={() => setFilter('correct')}>Correct</button>
                            <button data-filter="incorrect" className={filter === 'incorrect' ? 'active' : ''} onClick={() => setFilter('incorrect')}>Incorrect</button>
                        </div>
                        {filtered.length === 0 ? (
                            <p className="picks-empty">No predictions found.</p>
                        ) : (
                            <div className="picks-list">
                                <div className="picks-row picks-heading">
                                    <span>Match</span>
                                    <span>Your Pick</span>
                                    <span>Result</span>
                                </div>
                                {filtered.map(p => (
                                    <div key={p._id} className="picks-row">
                                        <span>{p.matchId.split('-').slice(0, 2).join(' vs ')}</span>
                                        <span className="pick-selection">
                                            {p.selection === 'draw' ? 'Draw'
                                            : p.selection === 'home' ? `${p.matchId.split('-')[0]} Win`
                                            : `${p.matchId.split('-')[1]} Win`}
                                        </span>
                                        <span>
                                            {p.actualWinner === null ? 'Pending ⏳ '
                                            : p.actualWinner === 'draw' ? `Draw ${p.isCorrect ? '✅' : '❌'}`
                                            : p.actualWinner === 'home' ? `${p.matchId.split('-')[0]} Win ${p.isCorrect ? '✅' : '❌'}`
                                            : `${p.matchId.split('-')[1]} Win ${p.isCorrect ? '✅' : '❌'}`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/**
             * Football Rival Predictions Section
             * - Displays user's selected rival votes per team
             */}
            <section className="picks-box">
                <div className="picks-header" onClick={() => setRivalOpen(!rivalOpen)}>
                    <span className="picks-heading">My Football Predictions</span>
                    <span className="picks-arrow">{rivalOpen? '▲' : '▼'}</span>
                </div>

                {rivalOpen && (
                    <div className="picks-body">
                        {rivalVotes.length === 0 ? (
                            <p className="picks-empty">No football predictions yet.</p>
                        ) : (
                            <div className="picks-list">
                                <div className="picks-row picks-heading">
                                    <span>Team</span>
                                    <span>Rival Game Vote</span>
                                </div>
                                {rivalVotes.map((v,i)=> (
                                    <div key={i} className="picks-row">
                                        <span>{v.teamName}</span>
                                        <span>{v.rivalVote}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </section>

        </main>
    );
};


export default Home;